import { createFileRoute } from "@tanstack/react-router";
import { ServicePage, serviceHead } from "@/components/site/ServicePage";
import ladestandereHero from "@/assets/ladestandere-hero.jpg.asset.json";

const SLUG = "ladestandere";
const TITLE = "Ladestandere & ladeboks til elbil, installation i København";
const DESC =
  "Autoriseret installation af ladestander og ladeboks til elbil. Vi rådgiver om type, effekt og tilskud, privat, ejerforening og erhverv i hele Storkøbenhavn.";

export const Route = createFileRoute("/ladestandere")({
  head: () => serviceHead(SLUG, TITLE, DESC),
  component: () => (
    <ServicePage
      slug={SLUG}
      heroVariant="split"
      accentColor="oklch(0.72 0.16 130)"
      heroImageSrc={ladestandereHero.url}
      eyebrow="Ladestandere"
      title="Ladestandere til elbil, installeret af autoriseret elektriker"
      intro="Vi installerer ladeboks og ladestander hjemme, i ejerforeningen og på virksomheden. Vi rådgiver dig om effekt, kabelføring, lastbalancering og om tilskud kan opnås, og giver altid fast pris efter besigtigelse."
      stats={[
        { value: "11 kW", label: "Typisk effekt hjemme" },
        { value: "22 kW", label: "Maks. trefaset" },
        { value: "<1 dag", label: "Installation" },
        { value: "Garanti", label: "På installationen" },
      ]}
      longIntro={
        <>
          <h2>Hvorfor en fast ladeboks frem for stikkontakt?</h2>
          <p>
            En almindelig stikkontakt på 230V og 13A leverer ca. 2,3 kW og er hverken designet til
            mange timers træk i træk eller til den mekaniske belastning et tungt ladekabel giver.
            En dedikeret ladeboks kører på 3-faset 400V, leverer typisk 11 kW (16A) eller 22 kW
            (32A), har egen jordfejlsbeskyttelse Type B (krav til DC-fejlstrømme fra elbiler) og
            kobler sig op på dit hjems lastbalancering, så hovedsikringen ikke springer når
            ovn, induktion og bil trækker samtidig.
          </p>
          <p>
            Vi har installeret hundredvis af ladebokse i Storkøbenhavn, fra enkle hjemmeløsninger
            i en carport til store, lastbalancerede ladeparker i ejerforeninger og på erhverv. Vi
            arbejder med de gængse anerkendte mærker (Easee, Wallbox, Zaptec, Defa, Garo m.fl.) og
            anbefaler den løsning, der passer bedst til <em>din</em> bil, dit forbrug og dit el-system,
            ikke den vi har på lager.
          </p>
        </>
      }
      bullets={[
        "Installation udført af autoriseret elektriker, CVR 35209468",
        "Rådgivning om Easee, Wallbox, Zaptec, Defa, Garo m.fl.",
        "Lastbalancering så hovedsikringen ikke springer",
        "Type B jordfejlsafbryder (lovkrav til ladestandere)",
        "Privat, ejerforening og erhverv, også flådeløsninger",
        "Vi hjælper med ansøgning om afgiftsrefusion",
        "Garanti på installationen",
      ]}
      features={[
        { title: "Hjemmeladning", body: "Den billigste kWh er hjemme, vi finder den rette effekt til dit hus og din bil og fører kabel skånsomt." },
        { title: "Ejerforening", body: "Fælles ladeløsninger med individuel afregning, lastbalancering og adgangsstyring via brik eller app." },
        { title: "Erhverv & flåde", body: "Ladeparker til medarbejderbiler og firmaflåder med back-end, afregning og driftsovervågning." },
        { title: "Trefaset 11/22 kW", body: "Hurtigere ladning på trefaset, typisk fuld bil på 4-8 timer i stedet for et helt døgn." },
        { title: "Lastbalancering", body: "Dynamisk effektstyring sikrer at bilen lader så hurtigt som muligt uden at overbelaste tavlen." },
        { title: "Solcelle-integration", body: "Lad bilen primært på din egen solstrøm, vi kobler ladeboks og inverter sammen." },
      ]}
      process={[
        { title: "Besigtigelse", body: "Vi gennemgår tavle, hovedsikring, kabelvej og ønsker, og giver dig en realistisk anbefaling." },
        { title: "Fast tilbud", body: "Du får skriftligt tilbud med fast pris på model, effekt, kabelføring og evt. opgradering af tavle." },
        { title: "Installation", body: "Typisk udført på en halv til en hel dag. Vi opretter, idriftsætter og tester ladning sammen med dig." },
        { title: "Dokumentation", body: "Du modtager KLS-rapport, oplæg til afgiftsrefusion og kort intro til app og adgangsstyring." },
      ]}
      faq={[
        { q: "Hvilken ladestander skal jeg vælge?", a: "Det afhænger af bil, hovedsikring, om I er flere brugere og om I vil have app-styring. Vi gennemgår mulighederne med dig før vi anbefaler, og vi presser ikke et bestemt mærke ned over dig." },
        { q: "Hvad koster en ladeboks?", a: "Typisk 12.000–22.000 kr. inkl. moms og montering for en privatinstallation, afhængigt af mærke, effekt og kabelafstand. Lastbalancering og opgradering af tavle kan komme oveni. Vi giver skriftligt tilbud med fast pris efter besigtigelse." },
        { q: "Kan jeg få tilskud?", a: "Du kan få afgiftsrefusion på elafgiften på den strøm der lader bilen (i 2025: ca. 95 øre/kWh refunderes), forudsat at ladeboksen har separat måler. Vi sætter dig op til at kunne ansøge, og hjælper med at vælge en leverandør der håndterer det automatisk." },
        { q: "Skal jeg have ny tavle eller hovedsikring?", a: "Ofte ikke, hvis du har trefaset og en moderne tavle. Vi måler belastning og ser om hovedsikringen kan klare det, og foreslår evt. lastbalancering eller opgradering hvis nødvendigt." },
        { q: "Kan I installere i en ejerforening?", a: "Ja, vi har leveret mange foreningsløsninger med fælles infrastruktur og individuel afregning. Vi kan både præsentere på generalforsamling og stå for hele udrulningen." },
        { q: "Hvor lang tid tager det?", a: "En typisk privatinstallation er 4-8 timer. Større foreningsløsninger med kabling til mange p-pladser strækker sig over flere dage." },
      ]}
      related={[
        { to: "/groen-energi", title: "Grøn energi", body: "Solceller og batteri der kan oplade bilen direkte fra dit eget tag." },
        { to: "/energioptimering", title: "Energioptimering", body: "Sænk dit øvrige forbrug så elbilen ikke vælter regnskabet." },
        { to: "/priser-privat", title: "Priser privat", body: "Se vores faste timepriser og hvad ladestander typisk koster." },
      ]}
    />
  ),
});
